// ✅ src/pages/VehicleV2/BranchwiseVehicleReportListing.jsx
// UPDATED FULL FILE
//
// ✅ Updated pattern similar to OwnVehicleReport
// ✅ common localStorage / sessionStorage header pattern
// ✅ useMemo + useCallback pattern
// ✅ pageLoading + dropdownLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ safer search / reset / refresh / pagination / print flow
// ✅ stable own/hire vehicle mode handling

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { FaSearch, FaPrint, FaArrowLeft, FaInfoCircle, FaSyncAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/branchwiseMasterReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const list = toList(arr).map((x) => {
    if (typeof x === "string" || typeof x === "number") {
      return { label: String(x), value: String(x) };
    }

    return {
      label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
      value: String(x.value ?? x.id ?? x.key ?? ""),
      ...x,
    };
  });

  if (!list.length) {
    return [{ label: fallbackLabel, value: "" }];
  }

  return list;
};

const defaultFilters = {
  month: "",
  year: "",
  vehtype: "",
  status: "0",
};

export default function BranchwiseVehicleReport() {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);
  const [vehicleType, setVehicleType] = useState("own"); // own | hire

  /* ===========================
     meta/dropdown states
  =========================== */
  const [meta, setMeta] = useState({
    months: [{ label: "Select", value: "" }],
    years: [{ label: "Select", value: "" }],
  });

  /* ===========================
     filters + list states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalRows, setTotalRows] = useState(0);

  /* ===========================
     computed
  =========================== */
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRows / pageSize)),
    [totalRows, pageSize]
  );

  const columns = useMemo(
    () => [
      { key: "BranchName", label: "Branch" },
      { key: "VehicleType", label: "Vehicle Type" },
      { key: "Qty", label: "Qty" },
      // { key: "VehicleTypeIndex", label: "Type Index" },
      // { key: "VehicleTypeId", label: "Type Id" },
      // { key: "VehicleMainTypeId", label: "Main Type Id" },
    ],
    []
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const inputHeadClass = useMemo(() => `${buttonClass} text-white`, [buttonClass]);

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const tdClass = useMemo(
    () =>
      `px-3 py-2 text-sm text-center whitespace-nowrap ${
        isDark ? "text-gray-200" : "text-gray-900"
      }`,
    [isDark]
  );

  const thClass = "px-3 py-3 text-sm font-semibold text-center whitespace-nowrap";

  /* ===========================
     helpers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const vehmode = useMemo(
    () => (vehicleType === "hire" ? "H" : "O"),
    [vehicleType]
  );

  /* ===========================
     meta load
  =========================== */
  const loadMeta = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const { data } = await axios.get(`${API_BASE}/meta`, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load meta");
      }

      setMeta({
        months: normalizeOptionList(data.months, "Select"),
        years: normalizeOptionList(data.years, "Select"),
      });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Failed to load meta");
      setMeta({
        months: [{ label: "Select", value: "" }],
        years: [{ label: "Select", value: "" }],
      });
    } finally {
      setDropdownLoading(false);
    }
  }, [commonHeaders]);

  /* ===========================
     list fetch
  =========================== */
  const fetchList = useCallback(
    async (opts = {}, nextFilters = filters, nextVehicleType = vehicleType) => {
      try {
        setListLoading(true);

        const targetPage = opts.page ?? page;
        const mode = nextVehicleType === "hire" ? "H" : "O";

        const { data } = await axios.get(`${API_BASE}/listing`, {
          params: {
            vehmode: mode,
            vehtype: nextFilters.vehtype || "",
            month: nextFilters.month || "",
            year: nextFilters.year || "",
            status: nextFilters.status || "%",
            page: targetPage,
            pageSize,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load report");
        }

        setRows(toList(data.rows));
        setTotalRows(toInt(data.totalRows, 0));
        setPage(toInt(data.page, targetPage));
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        setRows([]);
        setTotalRows(0);
      } finally {
        setListLoading(false);
      }
    },
    [filters, vehicleType, page, pageSize, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(defaultFilters);
      setRows([]);
      setTotalRows(0);
      setPage(1);

      await loadMeta();
    } finally {
      setPageLoading(false);
    }
  }, [loadMeta]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setField(name, value);
  }, [setField]);

  const handleSearch = useCallback(async () => {
    await fetchList({ page: 1 }, filters, vehicleType);
    toast.success("Report loaded");
  }, [fetchList, filters, vehicleType]);

  const handleView = useCallback(async () => {
    await fetchList({ page: 1 }, filters, vehicleType);
  }, [fetchList, filters, vehicleType]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleReset = useCallback(() => {
    setFilters(defaultFilters);
    setRows([]);
    setTotalRows(0);
    setPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, []);

  const handlePrint = useCallback(() => {
    navigate("/vehicle/BranchwiseMasterReport_Print", {
      state: {
        vehmode,
        month: filters.month,
        year: filters.year,
        status: filters.status,
        vehtype: filters.vehtype,
      },
    });
  }, [navigate, vehmode, filters]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleVehicleTypeChange = useCallback(async (value) => {
    setVehicleType(value);
    setRows([]);
    setTotalRows(0);
    setPage(1);
    await fetchList({ page: 1 }, filters, value);
  }, [fetchList, filters]);

  const goPage = useCallback(
    async (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      await fetchList({ page: next }, filters, vehicleType);
    },
    [fetchList, filters, vehicleType, totalPages]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    (async () => {
      await loadPage();
      await fetchList({ page: 1 }, defaultFilters, "own");
    })();
  }, [loadPage, fetchList]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BRANCHWISE VEHICLE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Branchwise Vehicle Report (Listing)
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  Total Records: {totalRows}
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-purple-900 text-purple-100" : "bg-purple-100 text-purple-800"
                  }`}
                >
                  Mode: {vehicleType === "hire" ? "Hire Vehicle" : "Own Vehicle"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || dropdownLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-800"
                }`}
              >
                Loading...
              </h3>
              <p
                className={`mb-6 max-w-sm mx-auto ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    FILTRATION AREA
                  </h3>

                  {/* radio */}
                  <div className="flex gap-6 items-center mb-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={vehicleType === "own"}
                        onChange={() => handleVehicleTypeChange("own")}
                        disabled={isBusy}
                      />
                      Own Vehicle
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={vehicleType === "hire"}
                        onChange={() => handleVehicleTypeChange("hire")}
                        disabled={isBusy}
                      />
                      Hire Vehicle
                    </label>
                  </div>

                  {/* filters */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelClass}>Month</label>
                      <select
                        name="month"
                        value={filters.month}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {meta.months.map((m, i) => (
                          <option key={`${m.value}-${i}`} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Year</label>
                      <select
                        name="year"
                        value={filters.year}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {meta.years.map((y, i) => (
                          <option key={`${y.value}-${i}`} value={y.value}>
                            {y.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label className={labelClass}>Vehicle Main Type Ids (optional)</label>
                      <input
                        name="vehtype"
                        value={filters.vehtype}
                        onChange={handleChange}
                        placeholder="Example: 1,2,3"
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Status</label>
                      <select
                        name="status"
                        value={filters.status}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="%">All</option>
                        <option value="0">0 (Not Removed / Not Scrap)</option>
                        <option value="1">1 (Removed / Scrap)</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading
                        ? "Loading page..."
                        : dropdownLoading
                        ? "Loading dropdowns..."
                        : listLoading
                        ? "Loading list..."
                        : "Ready"}
                    </div>

                    <div className="flex gap-4 flex-wrap">
                      <button
                        onClick={handleReset}
                        type="button"
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        Clear / Reset
                      </button>

                      <button
                        onClick={handleView}
                        type="button"
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        Search / View
                      </button>

                      <button
                        onClick={handleSearch}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaSearch />
                        Search
                      </button>

                      <button
                        onClick={handlePrint}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaPrint />
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* listing grid */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {rows.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      No Records
                    </h3>
                    <p className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Use filters and click Search.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                      <thead className={inputHeadClass}>
                        <tr>
                          <th className={thClass}>S.No</th>
                          {columns.map((c) => (
                            <th key={c.key} className={thClass}>
                              {c.label}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {rows.map((r, idx) => (
                          <tr
                            key={`${r.BranchName ?? "row"}-${r.VehicleType ?? "type"}-${idx}`}
                            className={`${
                              isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                            } transition-colors duration-200`}
                          >
                            <td className={tdClass}>{(page - 1) * pageSize + idx + 1}</td>

                            {columns.map((c) => {
                              const v = r?.[c.key];
                              return (
                                <td key={c.key} className={tdClass}>
                                  {v === null || v === undefined || v === "" ? "0" : String(v)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* pagination */}
              {totalRows > 0 && (
                <div className="mt-6 flex justify-center items-center gap-3">
                  <button
                    className={`px-4 py-2 border rounded ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${isBusy || page <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(page - 1)}
                    disabled={isBusy || page <= 1}
                    type="button"
                  >
                    Prev
                  </button>

                  <div
                    className={`px-4 py-2 border rounded ${
                      isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
                    }`}
                  >
                    Page {page} / {totalPages}
                  </div>

                  <button
                    className={`px-4 py-2 border rounded ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${isBusy || page >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(page + 1)}
                    disabled={isBusy || page >= totalPages}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}